import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { createAdDraft } from '../../../services/p2pService';
import { useToast } from '../../../context/ToastContext';
import { getMarketPrice } from '../../../services/p2pService';

const CRYPTOS = ['USDT','BTC','ETH'];
const FIATS = ['KES','UGX','TZS','RWF','NGN','GHS','ZAR','USD','PKR','INR','PHP','VND','TRY','BRL','MYR','IDR','AED','SAR','EGP','MAD'];
const KEY = 'p2p_step1';

function load() {
  try { return JSON.parse(sessionStorage.getItem(KEY) || '{}'); } catch { return {}; }
}
function save(data: object) {
  sessionStorage.setItem(KEY, JSON.stringify(data));
}

export default function PostAdStep1() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const saved = load();

  const [side, setSide] = useState<'buy'|'sell'>(saved.side || 'buy');
  const [crypto, setCrypto] = useState(saved.crypto || 'USDT');
  const [fiat, setFiat] = useState(saved.fiat || 'KES');
  const [priceType, setPriceType] = useState<'fixed'|'floating'>(saved.priceType || 'fixed');
  const [fixedPrice, setFixedPrice] = useState(saved.fixedPrice || '');
  const [floatingMargin, setFloatingMargin] = useState(saved.floatingMargin || '0');
  const [loading, setLoading] = useState(false);
  const [marketPrice, setMarketPrice] = useState<number | null>(null);
  const [priceLoading, setPriceLoading] = useState(false);

  // Persist on every change
  useEffect(() => {
    save({ side, crypto, fiat, priceType, fixedPrice, floatingMargin });
  }, [side, crypto, fiat, priceType, fixedPrice, floatingMargin]);

  const fetchMarketPrice = async () => {
    setPriceLoading(true);
    try { const res = await getMarketPrice(crypto, fiat); setMarketPrice(res.price); }
    catch { setMarketPrice(null); }
    setPriceLoading(false);
  };

  useEffect(() => { fetchMarketPrice(); }, [crypto, fiat]);

  const equivalent = fixedPrice && marketPrice && parseFloat(fixedPrice) > 0
    ? (parseFloat(fixedPrice) / marketPrice).toFixed(6) : null;

  const floatingActual = marketPrice && floatingMargin !== ''
    ? (marketPrice * (1 + parseFloat(floatingMargin || '0') / 100)).toLocaleString(undefined, { maximumFractionDigits: 2 })
    : null;

  const useMarketPrice = () => { if (marketPrice) setFixedPrice(marketPrice.toFixed(2)); };

  const next = async () => {
    if (priceType === 'fixed' && !fixedPrice) { toast('Enter a price', 'error'); return; }
    setLoading(true);
    try {
      // Always create a fresh draft — clear any stale draft ID and step data
      sessionStorage.removeItem('p2p_draft_id');
      sessionStorage.removeItem('p2p_step2');
      sessionStorage.removeItem('p2p_step3');
      const draft = await createAdDraft({ side, crypto, fiat, priceType, fixedPrice: priceType==='fixed'?parseFloat(fixedPrice):undefined, floatingMargin: priceType==='floating'?parseFloat(floatingMargin):undefined });
      sessionStorage.setItem('p2p_draft_id', draft._id);
      navigate('/p2p/post-ad/2');
    } catch (e:any) { toast(e.message, 'error'); }
    setLoading(false);
  };

  return (
    <div style={{ background:'var(--p2p-bg-primary)', height:'100dvh', display:'flex', flexDirection:'column', overflow:'hidden' }}>
      <div className="p2p-header" style={{ flexShrink:0 }}>
        <button className="p2p-back-btn" onClick={()=>navigate(-1)}><ArrowLeft size={20}/>Post Ad</button>
      </div>
      <div style={{ flex:1, overflowY:'auto', padding:16, display:'flex', flexDirection:'column', gap:20 }}>
        <div className="p2p-steps">
          <div className="p2p-step-dot active">1</div>
          <div className="p2p-step-line"/>
          <div className="p2p-step-dot">2</div>
          <div className="p2p-step-line"/>
          <div className="p2p-step-dot">3</div>
        </div>

        <div className="p2p-trade-toggle">
          <button className={side==='buy'?'buy-active':''} onClick={()=>setSide('buy')}>Buy</button>
          <button className={side==='sell'?'sell-active':''} onClick={()=>setSide('sell')}>Sell</button>
        </div>

        <div>
          <div style={{ fontSize:13, color:'var(--p2p-text-muted)', marginBottom:8 }}>Cryptocurrency</div>
          <div className="p2p-chip-row">{CRYPTOS.map(c=><button key={c} className={`p2p-chip ${crypto===c?'selected':''}`} onClick={()=>setCrypto(c)}>{c}</button>)}</div>
        </div>

        <div>
          <div style={{ fontSize:13, color:'var(--p2p-text-muted)', marginBottom:8 }}>Fiat Currency</div>
          <select className="p2p-select" value={fiat} onChange={e=>setFiat(e.target.value)}>
            {FIATS.map(f=><option key={f} value={f}>{f}</option>)}
          </select>
        </div>

        <div>
          <div style={{ fontSize:13, color:'var(--p2p-text-muted)', marginBottom:8 }}>Price Type</div>
          <div className="p2p-chip-row">
            <button className={`p2p-chip ${priceType==='fixed'?'selected':''}`} onClick={()=>setPriceType('fixed')}>Fixed</button>
            <button className={`p2p-chip ${priceType==='floating'?'selected':''}`} onClick={()=>setPriceType('floating')}>Floating</button>
          </div>
        </div>

        {priceType==='fixed' ? (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
              <div style={{ fontSize:13, color:'var(--p2p-text-muted)' }}>Fixed Price ({fiat})</div>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                {marketPrice && <span style={{ fontSize:12, color:'var(--p2p-text-muted)' }}>Market: <strong style={{ color:'var(--p2p-text-primary)' }}>{marketPrice.toLocaleString(undefined,{maximumFractionDigits:2})}</strong></span>}
                {marketPrice && <button onClick={useMarketPrice} style={{ fontSize:11, padding:'3px 10px', borderRadius:6, border:'1px solid var(--p2p-buy)', background:'var(--p2p-buy-subtle)', color:'var(--p2p-buy)', cursor:'pointer', fontWeight:700 }}>Use</button>}
              </div>
            </div>
            <div style={{ position:'relative' }}>
              <input className="p2p-input" style={{ paddingRight: fiat.length * 9 + 16 }} placeholder={marketPrice ? `Market: ${marketPrice.toFixed(2)}` : 'Enter price'} value={fixedPrice} onChange={e=>setFixedPrice(e.target.value.replace(/[^0-9.]/g,''))} />
              <span style={{ position:'absolute', right:14, top:'50%', transform:'translateY(-50%)', fontSize:13, fontWeight:700, color:'var(--p2p-text-muted)', pointerEvents:'none' }}>{fiat}</span>
            </div>
            {equivalent && (
              <div style={{ marginTop:8, fontSize:13, color:'var(--p2p-text-muted)' }}>
                ≈ <strong style={{ color:'var(--p2p-text-primary)' }}>{equivalent} {crypto}</strong>
                {marketPrice && parseFloat(fixedPrice) > marketPrice && <span style={{ marginLeft:8, color:'var(--p2p-buy)', fontSize:12 }}>+{(((parseFloat(fixedPrice)-marketPrice)/marketPrice)*100).toFixed(2)}% above market</span>}
                {marketPrice && parseFloat(fixedPrice) < marketPrice && <span style={{ marginLeft:8, color:'var(--p2p-sell)', fontSize:12 }}>{(((parseFloat(fixedPrice)-marketPrice)/marketPrice)*100).toFixed(2)}% below market</span>}
              </div>
            )}
          </div>
        ) : (
          <div>
            <div style={{ fontSize:13, color:'var(--p2p-text-muted)', marginBottom:8 }}>Floating Margin (%)</div>
            <div style={{ position:'relative' }}>
              <input className="p2p-input" style={{ paddingRight:30 }} placeholder="e.g. 2" value={floatingMargin} onChange={e=>setFloatingMargin(e.target.value)} />
              <span style={{ position:'absolute', right:14, top:'50%', transform:'translateY(-50%)', fontSize:13, fontWeight:700, color:'var(--p2p-text-muted)', pointerEvents:'none' }}>%</span>
            </div>
            {floatingActual && <div style={{ marginTop:8, fontSize:13, color:'var(--p2p-text-muted)' }}>Current price: <strong style={{ color:'var(--p2p-buy)' }}>{floatingActual} {fiat}</strong></div>}
          </div>
        )}

      </div>
      <div style={{ flexShrink:0, padding:'12px 16px calc(12px + env(safe-area-inset-bottom)) 16px', marginBottom:'calc(64px + env(safe-area-inset-bottom))', borderTop:'1px solid var(--p2p-border-default)', background:'var(--p2p-bg-primary)' }}>
        <button className="p2p-btn-yellow" disabled={loading || (priceType==='fixed' && !fixedPrice) || (priceType==='floating' && floatingMargin==='')} onClick={next}>
          {loading ? 'Creating...' : 'Next'}
        </button>
      </div>
    </div>
  );
}
